import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { ASSETS, EVENTS } from '../../data/site';
import { Starfield } from '../Starfield';

// Balanced two-column hero on web: stacked headline + copy left, NEXT EVENT card
// as the right grid column (placement in polish.css). On mobile the card renders
// as a compact HORIZONTAL strip (flyer left, details right; flyer keeps native
// 16:9 — mobile.css). Headline: brand teal→purple gradient.
// EST EYEBROW — THE ROBUST MECHANISM (Ryan, Aug 29, after two JS-measurement
// attempts misbehaved on iOS): an inline SVG with textLength/lengthAdjust sits
// absolutely above the FUTURE word inside an inline-block .futcol wrapper.
// The wrapper's width IS the word's rendered width, the SVG is 96.4% of it
// (2% left inset compensates the F glyph's side bearing), and textLength forces
// the text to occupy exactly that width with uniform spacing — no JS, no font
// metrics, no canvas; it cannot bleed past FUTURE on any browser.
// ⚠ GRADIENT: background-clip:text does NOT survive across an inline-block
// child — FUTURE rendered INVISIBLE when the clip lived on the h1. The gradient
// therefore lives on each word span (.gw style), not on the h1.

const EST_TEXT = 'EST. 2015 · GULF COAST, USA';
const GRAD: React.CSSProperties = {
  backgroundImage: 'linear-gradient(100deg, #37e5ff 0%, #c86bff 48%, #8b5cff 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent'
};
export function Hero() {
  const ref = useRef<HTMLElement | null>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const next = EVENTS[0];
  const nextCover = next.cover.replace('/upload/', '/upload/f_auto,q_auto,w_700/');
  return <section id="top" ref={ref} className="relative flex min-h-[100svh] w-full items-center overflow-hidden" data-id="element-212" data-mp="35jp8ncv1sd54">
      <motion.div className="absolute inset-0 z-0" style={{
      y: bgY,
      scale: bgScale
    }} data-id="element-213" data-mp="2qu9seqt57q3o">
        <img src={ASSETS.heroNebula} alt="" aria-hidden="true" className="h-full w-full object-cover" data-id="element-214" data-mp="2vt4atrn77cn9" />
      </motion.div>
      <div aria-hidden="true" className="absolute inset-0 z-0" style={{
      background: 'linear-gradient(180deg, rgba(3,2,10,0.9) 0%, rgba(3,2,10,0.5) 34%, rgba(3,2,10,0.86) 78%, #03020a 100%)'
    }} data-id="element-215" data-mp="2mvaguncwov39" />
      <div aria-hidden="true" className="absolute inset-0 z-0" style={{
      background: 'linear-gradient(100deg, rgba(3,2,10,0.94) 0%, rgba(3,2,10,0.72) 34%, rgba(3,2,10,0.28) 60%, rgba(3,2,10,0) 82%)'
    }} data-id="element-216" data-mp="664ydboyc8vn" />
      <div aria-hidden="true" className="absolute inset-0 z-0" style={{
      background: 'radial-gradient(55% 60% at 18% 42%, rgba(55,229,255,0.12) 0%, rgba(3,2,10,0) 70%)'
    }} data-id="element-217" data-mp="1vna8itikitsj" />
      <Starfield className="pointer-events-none absolute inset-0 z-[1] h-full w-full" data-id="element-218" data-mp="20mdgalpxne47" />

      <motion.div style={{
      y: contentY,
      opacity: contentOpacity
    }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-32 sm:px-8 sm:pt-36" data-id="element-219" data-mp="2edi851n2723z">
        <motion.h1 initial={{
        opacity: 0,
        y: 26
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.85,
        delay: 0.08,
        ease: [0.23, 1, 0.32, 1]
      }} className="max-w-[12ch] font-display text-[clamp(2.1rem,8.4vw,7.2rem)] font-bold leading-[0.98] tracking-[-0.01em] text-white drop-shadow-[0_2px_40px_rgba(3,2,10,0.9)]" data-id="element-220" data-mp="2abn59sphcgo3">
          <span className="futcol relative inline-block" data-id="element-221" data-mp="ozvjo5gl2153">
            <svg className="pointer-events-none absolute" viewBox="0 0 200 9" role="img" aria-label={EST_TEXT} style={{
            bottom: 'calc(100% + 0.45rem)',
            left: '2%',
            width: '96.4%',
            height: 'auto',
            overflow: 'visible'
          }} data-id="element-222" data-mp="qfzlb9bumz2v">
              <text x="0" y="7.6" textLength="200" lengthAdjust="spacing" style={{
              fontFamily: "'Chakra Petch', 'Space Grotesk', system-ui, sans-serif",
              fontWeight: 600,
              fontSize: '7.6px',
              fill: '#37e5ff'
            }} data-id="element-223" data-mp="15cqbcrzmuyft">
                {EST_TEXT}
              </text>
            </svg>
            <span style={GRAD} data-id="element-224" data-mp="mfgrxaub5kyk">FUTURE</span>
          </span>{' '}
          <span style={GRAD} data-id="element-225" data-mp="3l334inl7bj7i">ASTRONAUT</span>
        </motion.h1>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-7 flex flex-col gap-3" data-id="element-226" data-mp="3q38wy5zap9qn">
          <p className="font-tech text-sm font-medium tracking-[0.28em] text-white/85 sm:text-base" data-id="element-227" data-mp="237ucr9fyb3on">
            EVENTS · RECORDS · STAGE PRODUCTION
          </p>
          <p className="font-script text-lg tracking-wide text-cyan sm:text-xl" data-id="element-228" data-mp="1t8lkqm4nbz4x">
            Dream · Love · Impact
          </p>
        </motion.div>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.32,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-8 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg" data-id="element-229" data-mp="1oath4uun2okw">
          A Gulf Coast electronic music collective throwing house, dubstep, riddim and drum &amp; bass
          shows, releasing records, and building the stages they happen on.
        </motion.p>

        {/* NEXT EVENT — cyan title bar on top; horizontal strip on mobile, right column on web */}
        <aside aria-label="Next event" className="next-ev relative mt-5 flex flex-wrap items-stretch overflow-hidden rounded-2xl border border-white/10 lg:mt-0 lg:block lg:w-[min(320px,24vw)]" style={{
        background: 'rgba(9,6,24,0.55)',
        backdropFilter: 'blur(18px)',
        boxShadow: '0 12px 40px rgba(3,2,10,0.45)'
      }} data-id="element-230" data-mp="qv0od8r300kf">
          <span className="block w-full basis-full bg-cyan px-3.5 py-1.5 font-tech text-[0.5rem] font-bold tracking-[0.22em] text-void lg:px-4 lg:py-2 lg:text-[0.55rem] lg:tracking-[0.26em]" data-id="element-231" data-mp="19nl21iy7lz3n">
            NEXT EVENT
          </span>
          <div className="w-[37%] flex-none overflow-hidden lg:aspect-video lg:w-auto" data-id="element-232" data-mp="10tzq07dfivxx">
            <img src={nextCover} alt={`Event artwork for ${next.title}`} className="h-full w-full object-cover" data-id="element-233" data-mp="14ua5qbr0x0pz" />
          </div>
          <div className="min-w-0 flex-1 p-3 lg:flex-none lg:p-3.5 lg:pb-4" data-id="element-234" data-mp="1xcxldbn93njo">
            <p className="font-script text-[0.8rem] text-cyan lg:text-[0.92rem]" data-id="element-235" data-mp="3i21eyvco1vkq">
              {next.date.toUpperCase()}
            </p>
            <h3 className="mt-1 font-display text-[0.66rem] font-semibold uppercase leading-[1.35] tracking-[0.05em] text-white lg:text-[0.85rem]" data-id="element-236" data-mp="3d2x13resr9o2">
              {next.title}
            </h3>
            <p className="mt-1 text-[0.62rem] text-white/55 lg:text-[0.74rem]" data-id="element-237" data-mp="3mtrrn7z2svfb">
              {next.venue} · {next.city}
            </p>
            <a href={next.tickets} target="_blank" rel="noopener noreferrer" className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-violet/40 bg-violet/10 px-3 py-1.5 font-tech text-[0.55rem] font-semibold tracking-[0.14em] text-white transition-[background-color,border-color,transform] duration-200 ease-expo hover:border-violet hover:bg-violet active:scale-[0.97] lg:mt-3 lg:rounded-xl lg:px-3.5 lg:py-2 lg:text-[0.64rem] lg:tracking-[0.16em]" data-id="element-238" data-mp="2z5i7sblfahqt">
              <span data-id="element-239" data-mp="2cwb5jp41l568">GET TICKETS</span>
              <ArrowUpRightIcon className="h-3 w-3 lg:h-3.5 lg:w-3.5" aria-hidden="true" data-id="element-240" data-mp="2ct6wadbpzj8p" />
            </a>
          </div>
        </aside>
      </motion.div>
    </section>;
}